(() => {
  class VanscapeRelatedCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-vsp-related-track]');
      this.nextButton = this.querySelector('[data-vsp-related-next]');
      this.abortController = new AbortController();
      this.scrollTimer = null;
      this.resizeFrame = null;
      this.currentIndex = 0;
      this.isReady = false;

      if (!this.track) return;

      this.originalItems = Array.from(this.track.children);
      this.originalCount = this.originalItems.length;
      if (this.originalCount < 2) return;

      this.createLoopSegments();
      this.nextButton?.addEventListener('click', () => this.moveBy(1), { signal: this.abortController.signal });
      this.track.addEventListener('keydown', (event) => this.onKeyDown(event), { signal: this.abortController.signal });
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true, signal: this.abortController.signal });
      this.track.addEventListener('scrollend', () => this.normalizePosition(), { signal: this.abortController.signal });

      this.resizeObserver = new ResizeObserver(() => this.scheduleResize());
      this.resizeObserver.observe(this.track);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      window.clearTimeout(this.scrollTimer);
      window.cancelAnimationFrame(this.resizeFrame);
    }

    createLoopSegments() {
      const beforeFragment = document.createDocumentFragment();
      const afterFragment = document.createDocumentFragment();

      this.originalItems.forEach((item) => {
        item.dataset.vspRelatedOriginal = '';
        beforeFragment.appendChild(this.createClone(item));
        afterFragment.appendChild(this.createClone(item));
      });

      this.track.prepend(beforeFragment);
      this.track.append(afterFragment);

      requestAnimationFrame(() => {
        this.measure();
        this.jumpToIndex(0);
        this.isReady = true;
      });
    }

    createClone(item) {
      const clone = item.cloneNode(true);
      clone.dataset.vspRelatedClone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    measure() {
      const items = Array.from(this.track.children);
      const firstMiddleItem = items[this.originalCount];
      const firstAfterItem = items[this.originalCount * 2];
      const secondMiddleItem = items[this.originalCount + 1];

      if (!firstMiddleItem || !firstAfterItem) return;

      this.middleStart = firstMiddleItem.offsetLeft;
      this.segmentWidth = firstAfterItem.offsetLeft - firstMiddleItem.offsetLeft;
      this.itemStep = secondMiddleItem
        ? secondMiddleItem.offsetLeft - firstMiddleItem.offsetLeft
        : firstMiddleItem.getBoundingClientRect().width;
    }

    jumpToIndex(index) {
      this.currentIndex = this.mod(index, this.originalCount);
      this.track.scrollTo({
        left: this.middleStart + (this.currentIndex * this.itemStep),
        behavior: 'auto'
      });
    }

    moveBy(direction) {
      if (!this.isReady || !this.itemStep) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({
        left: this.itemStep * direction,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    onKeyDown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.moveBy(document.dir === 'rtl' ? -1 : 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.moveBy(document.dir === 'rtl' ? 1 : -1);
      }
    }

    onScroll() {
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => this.normalizePosition(), 120);
    }

    normalizePosition() {
      if (!this.isReady || !this.segmentWidth || !this.itemStep) return;

      const relativePosition = this.track.scrollLeft - this.middleStart;
      const roundedIndex = Math.round(relativePosition / this.itemStep);
      this.currentIndex = this.mod(roundedIndex, this.originalCount);

      if (this.track.scrollLeft < this.middleStart - (this.itemStep / 2)) {
        this.track.scrollTo({ left: this.track.scrollLeft + this.segmentWidth, behavior: 'auto' });
      } else if (this.track.scrollLeft >= this.middleStart + this.segmentWidth - (this.itemStep / 2)) {
        this.track.scrollTo({ left: this.track.scrollLeft - this.segmentWidth, behavior: 'auto' });
      }
    }

    scheduleResize() {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        if (!this.isReady) return;
        const preservedIndex = this.currentIndex;
        this.measure();
        this.jumpToIndex(preservedIndex);
      });
    }

    mod(value, divisor) {
      return ((value % divisor) + divisor) % divisor;
    }
  }

  if (!customElements.get('vanscape-related-carousel')) {
    customElements.define('vanscape-related-carousel', VanscapeRelatedCarousel);
  }
})();
